import { customElement, property, state } from "lit/decorators.js";
import { PageElement, whenType } from "@/util/element.ts";
import { css, html } from "lit";
import { when } from "lit/directives/when.js";
import { StaticPages, StaticPagesContext, StaticPagesType } from "@/context/static-pages.ts";
import { consume } from "@lit/context";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { choose } from "lit/directives/choose.js";
import { font } from "@/util/fonts.ts";
import { cardDefault, layoutOffsetPadding } from "@/pages/product-page/utils.ts";

@customElement("payment-terms-page")
export class PaymentTermsPage extends PageElement {
  declare type: "sm" | "lg";

  @state()
  loading = true;

  @consume({ context: StaticPagesContext, subscribe: true })
  @property({ attribute: true })
  public staticPages!: StaticPages;

  render() {
    return html` ${this.renderContent()} ${when(this.loading, () => this.renderLoaderOverlay())} `;
  }

  connectedCallback() {
    super.connectedCallback();
    this.load();
  }

  async load() {
    await this.staticPages.fetchStaticPage(StaticPagesType.payment);
    this.loading = false;
  }

  renderContent() {
    return choose(this.type, [
      ["sm", this.renderContentSm],
      ["lg", this.renderContentLg],
    ]);
  }

  renderContentSm = () => {
    return html`
      <catalog-layout type=${this.type}>
        <div class="topcard">
          <div class="row-navigation">
            <fix-breadcrumb></fix-breadcrumb>
          </div>
          <div class="row-title">
            <div class="title">Payment</div>
          </div>
        </div>
        ${unsafeHTML(this.staticPages.getStaticPage(StaticPagesType.payment))}
      </catalog-layout>
    `;
  };

  renderContentLg = () => {
    return html`
      <catalog-layout type=${this.type}>
        <div class="topcard">
          <div class="row-navigation">
            <fix-breadcrumb></fix-breadcrumb>
          </div>
          <div class="row-title">
            <div class="title">Payment</div>
          </div>
        </div>
        ${unsafeHTML(this.staticPages.getStaticPage(StaticPagesType.payment))}
      </catalog-layout>
    `;
  };

  renderLoader() {
    return html`<fix-loader></fix-loader>`;
  }

  renderLoaderOverlay() {
    return html` <div class="overlay">${this.renderLoader()}</div> `;
  }

  static stylesCommon = css``;

  static stylesSm = css`
    .topcard .title {
      ${font("Heading/3")};
      margin: 8px;
    }
    .row {
      display: flex;
      margin: 4px 0;
    }
    .left {
      width: 40%;
    }
    .section {
      ${font("Base/Normal")}
    }
    .static-page-content {
      padding: 8px;
      background: #fff;
      margin: 8px 0;
    }
    .section-title-lg {
      ${font("Heading/3")}
      margin-bottom: 16px;
    }
    .section-title {
      ${font("Heading/5")}
      margin-bottom: 16px;
      margin-top: 16px;
    }
  `;

  static stylesLg = css`
    .topcard .title {
      ${font("Heading/2")};
    }
    .row {
      display: flex;
      margin: 4px 0;
    }
    .left {
      width: 150px;
    }
    .section {
      ${font("Base/Normal")}
    }

    .section-title {
      ${font("Heading/4")}
    }
    .li-title {
      ${font("Heading/5")}
      display: inline-block;
    }

    .topcard {
      ${cardDefault()};
      ${layoutOffsetPadding()};
      padding-top: 16px;
      padding-bottom: 16px;
      margin-top: 8px;
      margin-bottom: 8px;
    }
    .static-page-content {
      margin: 32px 48px;
      padding: 32px;
      background: #fff;
      border-radius: 6px;
    }
    .section-divider {
      background: #f0f0f0;
      height: 1px;
      margin: 32px 0px;
    }

    .ul-dashed span:not(.li-title),
    p {
      display: block;
      margin: 4px 0 0;
    }

    .ul-dashed {
      margin: 0;
      padding: 0;
    }
  `;

  static get styles() {
    return [this.stylesCommon, whenType("sm", this.stylesSm), whenType("lg", this.stylesLg)];
  }
}
